import { canonicalJsonbText } from './canonicalJsonb';
import {
  buildClientAppPlanFromConsultation,
  diagnoseClientAppPlan,
} from './clientAppMapper';
import { computeProgramCompleteness, PIECE_STATUS } from './programCompleteness';

export const PLAN_DELIVERY_CONTRACT_VERSION = 1;

const SHA256_RE = /^[0-9a-f]{64}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function resolveSealedFollowupWeek(consultation, client = null) {
  const isFollowup = consultation?.isFollowup === true || consultation?.is_followup === true;
  if (!isFollowup) return 0;
  const provenance = consultation?.followupData?._versionProvenance
    ?? consultation?.followup_data?._versionProvenance
    ?? null;
  // Une modification demandée hors rendez-vous est applicable immédiatement.
  // Elle ne doit jamais être publiée sous un faux jalon S4/S8/S12.
  if (provenance?.trigger === 'manual_program_update') {
    return provenance.workflowAction === 'manual'
      && ['targeted', 'menus_recipes', 'full'].includes(provenance.updateScope)
      ? 0
      : null;
  }
  const week = Number(
    consultation?.followupWeek
    ?? consultation?.followupData?._followupWeek
    ?? consultation?.followup_data?._followupWeek,
  );
  const packType = client?.packType || client?.pack_type || '';
  const packStartedAtConfirmed = client?.packStartedAtConfirmed
    ?? client?.pack_started_at_confirmed;
  const maximum = packType === 'suivi_adn' && packStartedAtConfirmed === true ? 6 : 4;
  return Number.isInteger(week) && week >= 1 && week <= maximum ? week : null;
}

function pick(value, fallback = null) {
  return value === undefined ? fallback : value;
}

function normalizeClientForRender(client) {
  return {
    prenom: String(client?.prenom || ''),
    nom: String(client?.form?.nom || ''),
    pack_type: String(client?.pack_type ?? client?.packType ?? ''),
    langue: String(client?.langue || 'FR'),
    form: pick(client?.form, {}),
  };
}

// Métadonnées de relecture d'Anissa (validations de repas, confirmations de
// conflits, relecture des textes au regard d'exclusions en prose). Elles
// n'atteignent ni l'application ni le Word : elles sortent de l'empreinte
// du livrable, exactement comme côté base (migration V97.249, opérateur
// jsonb « - »). Une ligne sans ces clés garde donc la même empreinte.
export const RENDER_EXCLUDED_REVIEW_KEYS = Object.freeze(['narrative_prose_review', 'week_menus_review']);

function editorialOverridesForRender(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  if (!RENDER_EXCLUDED_REVIEW_KEYS.some((key) => Object.hasOwn(value, key))) return value;
  const projected = { ...value };
  for (const key of RENDER_EXCLUDED_REVIEW_KEYS) delete projected[key];
  return projected;
}

function normalizeConsultationForRender(client, consultation) {
  return {
    id: String(consultation?.id || ''),
    nutrition_plan: String(
      consultation?.nutrition_plan
      ?? consultation?.nutritionPlan
      ?? consultation?.plan_text
      ?? '',
    ),
    supplements: String(consultation?.supplements || ''),
    editorial_overrides: editorialOverridesForRender(pick(
      consultation?.editorial_overrides ?? consultation?.editorialOverrides,
      null,
    )),
    fiche_frigo_json: pick(
      consultation?.fiche_frigo_json ?? consultation?.ficheFrigoJson,
      null,
    ),
    intro_letter: pick(consultation?.intro_letter, null),
    meal_recipes: pick(consultation?.meal_recipes, null),
    protocol_phases: pick(consultation?.protocol_phases, null),
    active_phase_id: pick(consultation?.active_phase_id, null),
    date: pick(consultation?.date ?? consultation?.createdAt ?? consultation?.created_at, null),
    objective: String(
      client?.form?.objectif
      || client?.form?.objective
      || client?.form?.objectifPrincipalNutrition
      || '',
    ),
    is_followup: consultation?.is_followup ?? consultation?.isFollowup ?? false,
    followup_data: pick(consultation?.followup_data ?? consultation?.followupData, null),
    observations: String(consultation?.observations || ''),
    nutritional_observations: String(
      consultation?.nutritional_observations ?? consultation?.nutritionalObservations ?? '',
    ),
  };
}

async function sha256(value, cryptoApi = globalThis.crypto) {
  if (!cryptoApi?.subtle || typeof TextEncoder === 'undefined') {
    throw new Error('Empreinte du livrable indisponible.');
  }
  const digest = await cryptoApi.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function buildPlanRenderInputsV1(client, consultation) {
  if (!client?.id || !consultation?.id) {
    throw new Error('Version du plan introuvable.');
  }
  return {
    version: PLAN_DELIVERY_CONTRACT_VERSION,
    client_id: String(client.id),
    client: normalizeClientForRender(client),
    consultation: normalizeConsultationForRender(client, consultation),
  };
}

export function buildSealedInitialAppPayloadV1(client, consultation, appPlan) {
  const payload = {
    email: client?.form?.email || client?.email || null,
    first_name: client?.prenom?.trim() || 'Cliente',
    mode: appPlan?.mode ?? null,
    locale: appPlan?.locale ?? null,
    title: appPlan?.title ?? null,
    objective: appPlan?.objective ?? null,
    source_consultation_id: String(consultation?.id || ''),
    nutrition_plan: String(
      consultation?.nutrition_plan ?? consultation?.nutritionPlan ?? consultation?.plan_text ?? '',
    ),
    sections: appPlan?.sections ?? null,
    journey_phases: appPlan?.journey_phases ?? null,
    followup_week: resolveSealedFollowupWeek(consultation, client),
  };
  // Seal the exact JSON value that crosses the network. JSON serialization
  // drops deeply nested undefined object properties, so normalize before both
  // hashing and returning the payload used by the publication proxy.
  return JSON.parse(JSON.stringify(payload));
}

function buildReadiness(plan, diagnostic, completeness) {
  const days = Array.isArray(plan?.sections?.week_meals?.days)
    ? plan.sections.week_meals.days
    : [];
  const firstDayMeals = Array.isArray(days[0]?.meals) ? days[0].meals : [];
  const structure = plan?.sections?.week_meals?.structure || null;
  const pieces = completeness?.pieces || [];
  const blockers = [];
  if (days.length !== 7 || firstDayMeals.length === 0) {
    blockers.push('La structure alimentaire ne peut pas être extraite pour l’application.');
  }
  if (!structure?.id) {
    blockers.push('Choisis la structure des repas avant de valider le plan.');
  } else if (structure.complete !== true) {
    blockers.push('La structure des repas du plan ne correspond pas au choix d’Anissa.');
  }
  const warnings = pieces
    .filter((piece) => piece.status === PIECE_STATUS.TODO || piece.status === PIECE_STATUS.PARTIAL)
    .map((piece) => ({ key: piece.key, label: piece.label, detail: piece.detail }));
  return {
    ready: blockers.length === 0,
    blockers,
    warnings,
    summary: {
      days: days.length,
      meals_per_day: firstDayMeals.length,
      meal_slots: firstDayMeals.map((meal) => meal.slot),
      meal_structure_id: structure?.id || null,
      recipes: pieces.find((piece) => piece.key === 'recipes')?.detail || '',
      fridge_items: diagnostic?.summary?.fridge_items || 0,
      supplement_groups: diagnostic?.summary?.protocol_groups || 0,
      ready_pieces: completeness?.readyCount || 0,
      total_pieces: completeness?.applicableCount || 0,
    },
  };
}

export async function buildPlanDeliveryContractV1(client, consultation, cryptoApi = globalThis.crypto) {
  const renderInputs = buildPlanRenderInputsV1(client, consultation);
  const appPlan = buildClientAppPlanFromConsultation(client, consultation);
  const sealedAppPayload = buildSealedInitialAppPayloadV1(client, consultation, appPlan);
  const diagnostic = diagnoseClientAppPlan(appPlan);
  const completeness = computeProgramCompleteness(client, consultation);
  const readiness = buildReadiness(appPlan, diagnostic, completeness);
  const [renderInputsSha256, appArtifactSha256] = await Promise.all([
    sha256(canonicalJsonbText(renderInputs), cryptoApi),
    sha256(canonicalJsonbText(sealedAppPayload), cryptoApi),
  ]);
  return Object.freeze({
    version: PLAN_DELIVERY_CONTRACT_VERSION,
    renderInputs,
    renderInputsSha256,
    appArtifactSha256,
    appPlan,
    sealedAppPayload,
    diagnostic,
    completeness,
    readiness,
  });
}

export function validatePlanDeliveryEvidence(value, expected = {}) {
  if (!value || typeof value !== 'object' || Array.isArray(value)
      || value.valid !== true
      || !UUID_RE.test(value.evidenceId || '')
      || value.contractVersion !== PLAN_DELIVERY_CONTRACT_VERSION
      || !SHA256_RE.test(value.contentSha256 || '')
      || !SHA256_RE.test(value.renderInputsSha256 || '')
      || !SHA256_RE.test(value.appArtifactSha256 || '')
      || !['standard', 'external', 'mgd_v2'].includes(value.authorityKind)) {
    throw new Error('Preuve du livrable application invalide.');
  }
  if (expected.contentSha256 && value.contentSha256 !== expected.contentSha256) {
    throw new Error('Le plan a changé depuis sa validation.');
  }
  if (expected.renderInputsSha256 && value.renderInputsSha256 !== expected.renderInputsSha256) {
    throw new Error('Les données destinées au Word ou à l’application ont changé.');
  }
  if (expected.appArtifactSha256 && value.appArtifactSha256 !== expected.appArtifactSha256) {
    throw new Error('Le contenu de l’application a changé depuis sa validation.');
  }
  return Object.freeze({ ...value });
}

export async function readCurrentPlanDeliveryEvidence({
  client,
  consultation,
  db,
} = {}) {
  if (!client?.id || !consultation?.id || typeof db?.rpc !== 'function') {
    throw new Error('Preuve du livrable application indisponible.');
  }
  const contract = await buildPlanDeliveryContractV1(client, consultation);
  const { data, error } = await db.rpc('lire_preuve_livrable_plan', {
    p_client_id: client.id,
    p_consultation_id: consultation.id,
  });
  if (error || !data) throw new Error(error?.message || 'Preuve du livrable application indisponible.');
  const evidence = validatePlanDeliveryEvidence(data, {
    renderInputsSha256: contract.renderInputsSha256,
    appArtifactSha256: contract.appArtifactSha256,
  });
  return Object.freeze({ contract, evidence });
}

export function summarizePlanDeliveryContract(contract) {
  const sections = contract?.appPlan?.sections || {};
  const meals = sections.week_meals?.days?.[0]?.meals || [];
  return {
    meals: meals.map((meal) => ({
      slot: meal.slot,
      label: meal.slot_label || meal.slot,
      title: meal.title || '',
      alternatives: Array.isArray(meal.alternatives) ? meal.alternatives.length : 0,
    })),
    pieces: contract?.completeness?.pieces || [],
    readiness: contract?.readiness || { ready: false, blockers: [], warnings: [], summary: {} },
  };
}
